import { useState } from 'react';
import { donneesPersonnellesApi, monEspaceApi } from '../../api/resources';
import { useSession } from '../../api/session.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { telechargeJson } from '../../lib/sauvegarde';
import { C } from '../../theme/palette';

/** Message que l'employé adresse à la direction pour demander l'effacement. */
const DEMANDE_EFFACEMENT =
  'Je demande l’effacement de mes données personnelles (LPD, art. 32). ' +
  'Merci de me confirmer ce qui est effacé et ce que la loi impose de conserver.';

/**
 * Nom du fichier téléchargé : « mes-donnees-swisstech-2026-10-07.json ».
 *
 * @param {string} etabliLe instant auquel l'état a été dressé, ISO
 * @returns {string} le nom proposé au navigateur
 */
function nomDuFichier(etabliLe) {
  const jour = /^\d{4}-\d{2}-\d{2}/.exec(etabliLe || '');
  return 'mes-donnees-swisstech-' + (jour ? jour[0] : 'export') + '.json';
}

/**
 * Carte « Mes données (LPD) » : le droit d'accès et le droit à l'effacement,
 * pour la personne connectée.
 *
 * <p>Le serveur savait dresser l'état des données d'une personne depuis
 * longtemps, sans qu'aucun écran ne le propose. Chacun télécharge ici ce que
 * l'entreprise détient sur lui ; l'effacement, lui, ne se fait pas d'un geste
 * depuis son propre compte : la loi impose de garder certaines pièces dix
 * ans, et c'est la direction qui tranche ce qui s'efface. L'employé lui
 * adresse sa demande d'ici, par le fil de messages qui existe déjà.
 *
 * @returns {JSX.Element} la carte
 */
export function MesDonneesCard() {
  const { session } = useSession();
  const [etat, setEtat] = useState({ telechargement: 'repos', demande: 'repos', message: '' });
  // Le patron est la direction : il n'a personne à qui demander. Un compte
  // du bureau sans fiche n'a pas de fil de messages ; il s'adresse de vive
  // voix à la direction.
  const peutDemander = session?.role !== 'patron' && session?.employeId != null;

  const telecharge = async () => {
    setEtat((e) => ({ ...e, telechargement: 'envoi', message: '' }));
    try {
      const donnees = await donneesPersonnellesApi.moi();
      telechargeJson(donnees, nomDuFichier(donnees?.etabliLe));
      setEtat((e) => ({ ...e, telechargement: 'fait' }));
    } catch (err) {
      setEtat((e) => ({
        ...e,
        telechargement: 'echec',
        message: err?.message || 'Le téléchargement a échoué.',
      }));
    }
  };

  const demandeLEffacement = async () => {
    if (
      !window.confirm(
        'Demander l’effacement de tes données personnelles à la direction ?\n\n' +
          'Si elle y donne suite, ta fiche est rendue anonyme : c’est irréversible. ' +
          'Les pièces que la loi impose de conserver (décomptes de salaire, documents émis) restent.',
      )
    )
      return;
    setEtat((e) => ({ ...e, demande: 'envoi', message: '' }));
    try {
      await monEspaceApi.ecrit(DEMANDE_EFFACEMENT);
      setEtat((e) => ({ ...e, demande: 'fait' }));
    } catch (err) {
      setEtat((e) => ({
        ...e,
        demande: 'echec',
        message: err?.message || 'La demande n’est pas partie.',
      }));
    }
  };

  return (
    <Card style={{ marginBottom: 12 }}>
      <Bar icon="🗂" title="Mes données (LPD)" color={C.teal} />
      <div style={{ padding: '13px 15px', fontSize: 12, lineHeight: 1.65, color: C.ink }}>
        <div style={{ marginBottom: 10 }}>
          La loi sur la protection des données te donne le droit de savoir ce que l’entreprise
          détient sur toi. Le fichier reprend ton compte, ta fiche et tout ce qui te mentionne :
          pointages, congés, messages, documents.
        </div>
        <Btn
          label={
            etat.telechargement === 'envoi'
              ? 'Préparation…'
              : etat.telechargement === 'fait'
                ? '✓ Fichier téléchargé'
                : '⬇ Télécharger mes données'
          }
          color={etat.telechargement === 'fait' ? C.green : C.teal}
          full
          disabled={etat.telechargement === 'envoi'}
          onClick={telecharge}
        />
        <div style={{ marginTop: 14, fontSize: 11, color: C.grey }}>
          {peutDemander ? (
            <>
              Tu peux demander l’effacement de tes données. La direction rend alors ta fiche anonyme
              ; les décomptes de salaire et les documents émis restent, la loi impose de les garder
              dix ans.
            </>
          ) : session?.role === 'patron' ? (
            <>
              L’effacement des données d’un employé ou d’un client se fait depuis sa fiche, dans
              Équipe ou Clients : les pièces que la loi impose de garder dix ans restent.
            </>
          ) : (
            <>
              Pour demander l’effacement de tes données, adresse-toi à la direction : elle seule
              peut rendre une fiche anonyme, et la loi impose de garder certaines pièces dix ans.
            </>
          )}
        </div>
        {peutDemander && (
          <div style={{ marginTop: 8 }}>
            <Btn
              label={
                etat.demande === 'envoi'
                  ? 'Envoi…'
                  : etat.demande === 'fait'
                    ? '✓ Demande envoyée à la direction'
                    : '🗑 Demander l’effacement'
              }
              ghost
              color={etat.demande === 'fait' ? C.green : C.red}
              full
              disabled={etat.demande === 'envoi' || etat.demande === 'fait'}
              onClick={demandeLEffacement}
            />
          </div>
        )}
        {etat.message && (
          <div role="alert" style={{ marginTop: 8, fontSize: 11, color: C.red }}>
            {etat.message}
          </div>
        )}
      </div>
    </Card>
  );
}
